import { computed, ref } from 'vue'
import { api, ApiError } from '@/lib/api'
import type { Project, ProjectRole } from '@/lib/types'

const RANK: Record<ProjectRole, number> = { viewer: 1, editor: 2, project_admin: 3 }

// 'error' means the role could not be read, which is not the same as having no
// permission — the UI words them differently.
export type RoleStatus = 'idle' | 'loading' | 'ready' | 'error'

const currentRole = ref<ProjectRole | undefined>(undefined)
const status = ref<RoleStatus>('idle')

// Slug whose role is held, so navigating within a project doesn't refetch.
let resolvedSlug = ''

export function setProjectRole(project: Project) {
  currentRole.value = project.projectRole
  status.value = 'ready'
}

export function clearProjectRole() {
  currentRole.value = undefined
  status.value = 'idle'
  resolvedSlug = ''
}

export interface ResolveResult {
  ok: boolean
  /** Set only when the API definitively refused. */
  deniedStatus?: number
}

/**
 * Resolve the caller's role. Called once Clerk is ready — never from the
 * request path, where an auth wait would delay every request in the app.
 */
export async function resolveProjectRole(slug: string, force = false): Promise<ResolveResult> {
  if (!force && slug === resolvedSlug && status.value === 'ready') return { ok: true }

  status.value = 'loading'
  try {
    const project = await api.getProject(slug)
    resolvedSlug = slug
    setProjectRole(project)
    return { ok: true }
  } catch (e) {
    resolvedSlug = ''
    currentRole.value = undefined
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
      status.value = 'idle'
      return { ok: false, deniedStatus: e.status }
    }
    status.value = 'error'
    return { ok: false }
  }
}

export function useProjectRole() {
  // Only a known role grants permission; an unresolved one denies without
  // claiming the user is a viewer.
  function roleAtLeast(min: ProjectRole): boolean {
    if (status.value !== 'ready' || !currentRole.value) return false
    return RANK[currentRole.value] >= RANK[min]
  }

  const canEdit = computed(() => roleAtLeast('editor'))
  const canAdmin = computed(() => roleAtLeast('project_admin'))
  const isReadOnly = computed(() => status.value === 'ready' && currentRole.value === 'viewer')

  function reason(allowed: boolean, denied: string): string {
    if (allowed) return ''
    if (status.value === 'idle' || status.value === 'loading') return 'Checking your permissions…'
    if (status.value === 'error') return 'Could not load your permissions. Reload the page to try again.'
    return denied
  }

  const editReason = computed(() =>
    reason(canEdit.value, 'You have viewer (read-only) access. Ask a project admin for editor access to make changes.'),
  )
  const adminReason = computed(() => reason(canAdmin.value, 'Only project admins can do this.'))

  return { canEdit, canAdmin, isReadOnly, editReason, adminReason, status }
}
